import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApprovalWorkflow, WorkflowInput, WorkflowStepInput } from "@/pages/admin/approvals/hooks/useApprovals";

interface WorkflowModalProps {
  open: boolean;
  initial: ApprovalWorkflow | null;
  onClose: () => void;
  onSubmit: (input: WorkflowInput) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

// 审批流编辑弹窗：配置名称、适用席位门槛、启停与逐级审批节点（可增删）。
export default function WorkflowModal({ open, initial, onClose, onSubmit, notify }: WorkflowModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [minSeats, setMinSeats] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [steps, setSteps] = useState<WorkflowStepInput[]>([{ name: "", approverRole: "staff" }]);
  const [bulkSla, setBulkSla] = useState("24");
  const [bulkEscalate, setBulkEscalate] = useState("0");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name);
      setDescription(initial.description ?? "");
      setMinSeats(String(initial.min_seats ?? 0));
      setEnabled(initial.enabled !== false);
      setSteps(
        initial.steps.length > 0
          ? initial.steps.map((step) => ({
              name: step.name,
              approverRole: step.approver_role === "admin" ? "admin" : "staff",
              slaHours: step.sla_hours ?? 24,
              escalateHours: step.escalate_after_hours ?? 0,
            }))
          : [{ name: "", approverRole: "staff", slaHours: 24, escalateHours: 0 }],
      );
    } else {
      setName("");
      setDescription("");
      setMinSeats("0");
      setEnabled(true);
      setSteps([{ name: t("approvals.stepDefaultFirst"), approverRole: "staff", slaHours: 24, escalateHours: 0 }]);
    }
  }, [open, initial, t]);

  if (!open) return null;

  const updateStep = (index: number, patch: Partial<WorkflowStepInput>) => {
    setSteps((prev) => prev.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };

  const addStep = () => setSteps((prev) => [...prev, { name: "", approverRole: "staff", slaHours: 24, escalateHours: 0 }]);
  const removeStep = (index: number) => setSteps((prev) => prev.filter((_, i) => i !== index));

  // 整体默认时限：一键把提醒 / 升级时限批量应用到所有审批节点，避免逐级重复配置。
  const applyBulkTimeout = () => {
    const sla = bulkSla === "" ? 0 : Number(bulkSla);
    const escalate = bulkEscalate === "" ? 0 : Number(bulkEscalate);
    setSteps((prev) =>
      prev.map((step) => ({
        ...step,
        slaHours: Number.isFinite(sla) && sla >= 0 ? sla : 0,
        escalateHours: Number.isFinite(escalate) && escalate > 0 ? escalate : 0,
      })),
    );
    notify(t("approvals.bulkTimeoutApplied", { count: steps.length }), "success");
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      notify(t("approvals.workflowNameRequired"), "error");
      return;
    }
    const cleaned = steps
      .map((step) => ({
        name: step.name.trim(),
        approverRole: step.approverRole,
        slaHours: step.slaHours,
        escalateHours: step.escalateHours,
      }))
      .filter((step) => step.name);
    if (cleaned.length === 0) {
      notify(t("approvals.workflowStepsRequired"), "error");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        workflowId: initial?.id,
        name: name.trim(),
        description: description.trim(),
        minSeats: Number(minSeats) >= 0 ? Math.floor(Number(minSeats)) : 0,
        enabled,
        steps: cleaned,
      });
      notify(initial ? t("approvals.workflowUpdated") : t("approvals.workflowCreated"), "success");
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.saveFailed"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-foreground-950/40 px-4 py-8">
      <div className="flex max-h-full w-full max-w-lg flex-col rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            {initial ? t("approvals.editWorkflow") : t("approvals.newWorkflow")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <label className="block text-xs font-medium text-foreground-700">{t("approvals.fieldName")}</label>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("approvals.fieldNamePlaceholder")}
            className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />

          <label className="mt-3 block text-xs font-medium text-foreground-700">{t("approvals.fieldDesc")}</label>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
            maxLength={300}
            placeholder={t("approvals.fieldDescPlaceholder")}
            className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-foreground-700">{t("approvals.fieldMinSeats")}</label>
              <input
                type="number"
                min={0}
                value={minSeats}
                onChange={(event) => setMinSeats(event.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
              <p className="mt-1 text-[11px] text-foreground-500">{t("approvals.fieldMinSeatsHint")}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700">{t("approvals.fieldEnabled")}</label>
              <button
                type="button"
                onClick={() => setEnabled((value) => !value)}
                className={`mt-1.5 inline-flex w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2.5 text-sm transition-colors ${
                  enabled
                    ? "border-primary-300 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-100 text-foreground-600"
                }`}
              >
                {enabled ? t("approvals.enabled") : t("approvals.disabled")}
                <span className={`flex h-4 w-4 items-center justify-center ${enabled ? "text-primary-600" : "text-foreground-400"}`}>
                  <i className={enabled ? "ri-toggle-fill" : "ri-toggle-line"} />
                </span>
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-700">{t("approvals.stepsLabel")}</span>
            <button
              type="button"
              onClick={addStep}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-add-line" />
              </span>
              {t("approvals.addStep")}
            </button>
          </div>

          <div className="mt-3 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium text-secondary-900">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-alarm-warning-line" />
                </span>
                {t("approvals.bulkTimeoutTitle")}
              </span>
              <span className="text-[10px] text-secondary-700">{t("approvals.bulkTimeoutHint")}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <label className="block">
                <span className="block text-[11px] text-foreground-600">{t("approvals.bulkSlaLabel")}</span>
                <input
                  type="number"
                  min={0}
                  value={bulkSla}
                  onChange={(event) => setBulkSla(event.target.value)}
                  className="mt-1 h-8 w-24 rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] text-foreground-600">{t("approvals.bulkEscalateLabel")}</span>
                <input
                  type="number"
                  min={0}
                  value={bulkEscalate}
                  onChange={(event) => setBulkEscalate(event.target.value)}
                  className="mt-1 h-8 w-24 rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                />
              </label>
              <button
                type="button"
                onClick={applyBulkTimeout}
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-check-double-line" />
                </span>
                {t("approvals.bulkApply")}
              </button>
            </div>
          </div>

          <ul className="mt-2 flex flex-col gap-2">
            {steps.map((step, index) => (
              <li key={index} className="rounded-md border border-background-200 bg-background-100 p-2.5">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-[11px] font-semibold text-secondary-900">
                    {index + 1}
                  </span>
                  <input
                    type="text"
                    value={step.name}
                    onChange={(event) => updateStep(index, { name: event.target.value })}
                    placeholder={t("approvals.stepNamePlaceholder")}
                    className="h-9 min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <select
                    value={step.approverRole}
                    onChange={(event) => updateStep(index, { approverRole: event.target.value as "staff" | "admin" })}
                    className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="staff">{t("approvals.roleStaff")}</option>
                    <option value="admin">{t("approvals.roleAdmin")}</option>
                  </select>
                  <button
                    type="button"
                    disabled={steps.length <= 1}
                    onClick={() => removeStep(index)}
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 pl-8">
                  <label className="block">
                    <span className="flex items-center gap-1 text-[11px] text-foreground-600">
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className="ri-alarm-warning-line" />
                      </span>
                      {t("approvals.stepSlaLabel")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={step.slaHours}
                      onChange={(event) =>
                        updateStep(index, { slaHours: event.target.value === "" ? 0 : Number(event.target.value) })
                      }
                      className="mt-1 h-8 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="flex items-center gap-1 text-[11px] text-foreground-600">
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className="ri-arrow-up-double-line" />
                      </span>
                      {t("approvals.stepEscalateLabel")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={step.escalateHours}
                      onChange={(event) =>
                        updateStep(index, { escalateHours: event.target.value === "" ? 0 : Number(event.target.value) })
                      }
                      className="mt-1 h-8 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                    />
                  </label>
                </div>
                <p className="mt-1 pl-8 text-[10px] text-foreground-400">{t("approvals.stepTimeoutHint")}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-end gap-2 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            {t("approvals.cancel")}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
            </span>
            {saving ? t("approvals.saving") : t("approvals.save")}
          </button>
        </div>
      </div>
    </div>
  );
}